import { FormActionsMui } from '@app/components/form/FormActionsMui';
import { AudiogramStyleFields } from '@app/modules/admin/screens/fragments/manage/audiogram/components/AudiogramStyleFields';
import { useAudiogramTemplates } from '@app/modules/admin/screens/fragments/manage/audiogram/hooks/useAudiogramTemplates';
import { audiogramStyleSchema } from '@app/modules/admin/screens/fragments/manage/audiogram/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { audiogramDefaultStyleProps, AudiogramProps, audiogramSchema } from '@mi-front/audiogram';
import { AudiogramTemplate } from '@mi-front/core';
import { WatchField } from '@mi-front/ui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotesIcon from '@mui/icons-material/Notes';
import PaletteIcon from '@mui/icons-material/Palette';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { useFragment } from '../../../hooks/useFragment';
import { AUDIOGRAM_TARGET_VARIANTS } from '../consts';
import { AudiogramlPlayerInForm } from './AudiogramPlayerInForm';
import { TemplateSelector } from './TemplateSelector';

export const defaultValues = {
  ...audiogramDefaultStyleProps,
  titleText: '',
  variants: AUDIOGRAM_TARGET_VARIANTS.map((v) => v.value),
};

export const audiogramContentSchema = audiogramSchema.pick({
  titleText: true,
});

export const audiogramFormatSchema = z.object({
  variants: z.array(z.string()),
});

export const schema = z
  .object({
    data: audiogramStyleSchema.merge(audiogramContentSchema).merge(audiogramFormatSchema),
  })
  .extend({
    templateId: z.string().optional(),
  });

export type AudiogramFormValues = z.infer<typeof schema> & {
  templates: AudiogramTemplate[];
};

interface Props {
  playerProps: Partial<AudiogramProps>;
  onSuccess: (data: AudiogramFormValues) => void;
}

export function AudiogramGenerator(props: Props) {
  const { $t } = useIntl();
  const { station } = useFragment();
  const { templates } = useAudiogramTemplates(station.id);

  const defaultTemplate = templates.find((t) => t.default);

  return (
    <div>
      <FormContainer<AudiogramFormValues>
        defaultValues={{
          data: {
            ...defaultValues,
            ...defaultTemplate?.data,
            titleText: props.playerProps.titleText,
          },
          templates,
          templateId: defaultTemplate?.name,
        }}
        onSuccess={props.onSuccess}
        resolver={zodResolver(schema)}
      >
        <Grid container spacing={2}>
          <Grid item md={7}>
            <Stack spacing={2} mb={3}>
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="title-content"
                  id="title-header"
                >
                  <Typography>
                    <NotesIcon />{' '}
                    {$t({
                      id: 'audiogram.content',
                      defaultMessage: 'Contenido',
                    })}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextFieldElement
                    name="data.titleText"
                    label={$t({
                      id: 'audiogram.titleText',
                      defaultMessage: 'Texto del título',
                    })}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="colors-content"
                  id="colors-header"
                >
                  <Typography>
                    <PaletteIcon />{' '}
                    {$t({
                      id: 'audiogram.style',
                      defaultMessage: 'Estilo',
                    })}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={3}>
                    <TemplateSelector />
                    <AudiogramStyleFields fieldsPrefix="data." />
                  </Stack>
                </AccordionDetails>
              </Accordion>
              <FormActionsMui
                saveLabel={$t({
                  id: 'audiogram.generate',
                  defaultMessage: 'Generar',
                })}
                saveIcon={<VideoFileIcon />}
                disableOnSubmit
              />
            </Stack>
          </Grid>

          <Grid item md={5}>
            <Box mx={3}>
              <WatchField name="data.variants">
                {(variants) => (
                  <AudiogramlPlayerInForm variants={variants} {...props.playerProps} />
                )}
              </WatchField>
            </Box>
          </Grid>
        </Grid>
      </FormContainer>
    </div>
  );
}
